import {useCallback} from 'react';
import {useGtagEventLocation} from 'hooks/analytics/gtag/helpers/useGtagEventLocation';
import {
    EventAction,
    EventCategory,
    EventContextType,
    EventCurrencyCodes,
    EventNames,
    EventPortalName,
} from 'modules/google-analytics/constants';
import {IAnalyticsMeta} from 'core/types';
import {getProductCartMainData, getProductEcommerceData} from 'modules/product-card/analytics/gtag/helpers';
import {IProduct} from 'modules/product-card/models/ProductResponse';
import {IReviewsTotal} from 'modules/product-card/models/Review';
import {sendGtagEvent} from 'hooks/analytics/gtag/helpers';
import {getProductBrand, getProductCanonSection} from 'modules/product-card/helpers';
import {useGetCartQuery} from 'modules/cart/services';
import {getFlocktoryInstance} from 'core/helpers';
import {buildProductStoreEventContext} from 'modules/analytics/helpers/buildProductStoreEventContext';
import {getStoreEventContext} from 'modules/analytics/helpers/getStoreEventContext';

export const useCartEventAnalytics = () => {
    const {eventLocation} = useGtagEventLocation();
    const {data: cart} = useGetCartQuery({mini: true});

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const getCartCount = (product: IProduct) =>
        cart?.products.find((p) => p.offerId === product.offerId)?.cartData?.quantity ?? 0;

    const getEventMeta = (isAdd: boolean) => ({
        eventAction: isAdd ? EventAction.Add : EventAction.Click,
        eventCategory: isAdd ? EventCategory.Conversions : EventCategory.Interactions,
    });

    const buildEcommerce = (product: IProduct, count: number, action: (typeof EventAction)[string], list?: string) => ({
        currencyCode: EventCurrencyCodes.RUB,
        [action]: {
            actionField: {list},
            products: [
                {
                    ...getProductEcommerceData(product, count),
                    list,
                },
            ],
        },
    });

    const pushFlocktory = (product: IProduct, count: number, isAdd: boolean) => {
        try {
            const flocktory = getFlocktoryInstance();
            const categoryId = getProductCanonSection(product.sections || [])?.id ?? 0;

            const price = product.price?.specPrice || product.price?.discount?.price || product.price?.base?.price || 0;

            const brand = getProductBrand(product);

            const baseItem = {
                id: String(product.offerId),
                count,
            };

            const payload = isAdd
                ? {
                      ...baseItem,
                      price,
                      brand,
                      categoryId,
                  }
                : baseItem;

            flocktory.push([isAdd ? 'addToCart' : 'removeFromCart', {item: payload}]);
        } catch {}
    };

    return useCallback(
        // eslint-disable-next-line complexity
        (
            product: IProduct | null | undefined,
            quantity: number = 1,
            analytics: IAnalyticsMeta = {positionIndex: 1},
            reviewsData?: IReviewsTotal | null,
        ) => {
            if (!product) {
                return;
            }

            const isProductPage = 'PRODUCT_PAGE' === eventLocation;
            const cartCount = getCartCount(product);
            const countDiff = Math.abs(cartCount - quantity);

            if (0 === countDiff) {
                return;
            }

            const action = quantity > cartCount ? EventAction.Add : EventAction.Remove;

            const isAdd = action === EventAction.Add;
            const listName = analytics.listName ?? eventLocation ?? undefined;

            const ecommerce = buildEcommerce(product, countDiff, action, listName);

            const eventMeta = getEventMeta(isAdd);

            const basePayload = {
                event: EventNames.OWOX,
                eventLocation,
                ...eventMeta,
                ...getProductCartMainData(product, isProductPage ? 1 : analytics.positionIndex || 1, action),
                ecommerce,
            };

            const payload = isProductPage
                ? {
                      ...basePayload,
                      eventContext: [
                          {
                              pageType: EventContextType.ProductPage,
                              ...buildProductStoreEventContext(getStoreEventContext(product)),
                          },
                      ],
                      eventPortalName: reviewsData ? EventPortalName.ProductReview : EventPortalName.Product,
                  }
                : {
                      ...basePayload,
                      eventPortalName: EventPortalName.Shop,
                  };

            sendGtagEvent(payload);

            pushFlocktory(product, countDiff, isAdd);
        },
        [eventLocation, getCartCount],
    );
};
